import { ApiError } from "@/client/extend";
import { OptionType } from "@/components/select-input/select-input";
import { FieldPath, FieldValues, UseFormReturn } from "react-hook-form";
import { GroupBase } from "react-select";
import { FormItemCommonProps, FormItemContent } from "../common";
import {
  SelectInputAsyncPaginateMutationCreatable,
  SelectInputAsyncPaginateMutationCreatableProps,
  SelectInputAsyncPaginateQueryCreatable,
  SelectInputAsyncPaginateQueryCreatableProps,
} from "@/components/select-input/select-input-async-pagination-creatable";
import {
  FieldOptionsCacheProps,
  FieldOptionsExtractProps,
  FieldOptionsMappingProps,
  SelectInputOptionsMappingProps,
  useFieldOptionsExtract,
  useFieldOptionsMapping,
  useFieldOptionsPartial,
  useFormSelectInputCommonClassNames,
} from "./common";
import { FormField } from "@/components/ui/form";
import { QueryKey } from "@tanstack/react-query";
import { useOptionsCache } from "@/components/select-input/options-cache";
import React from "react";
import { callAll, cn } from "@/lib/utils";

export type FormSelectInputAsyncPaginateMutationCreatableProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  TContext,
  TTransformedValues extends FieldValues | undefined,
  Value,
  Option extends OptionType<Value>,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
  TData,
  TError,
  TVariables,
  TContext2,
> = {
  form: UseFormReturn<TFieldValues, TContext, TTransformedValues>;
  name: TName;
} & FormItemCommonProps &
  Omit<
    SelectInputOptionsMappingProps<TFieldValues, TName, Value, Option, IsMulti>,
    "isMulti"
  > &
  Omit<
    SelectInputAsyncPaginateMutationCreatableProps<
      Value,
      Option,
      IsMulti,
      Group,
      TData,
      TError,
      TVariables,
      TContext2
    >,
    "form"
  > & {
    inline?: boolean;
    displaySelected?: boolean;
    isActive?: boolean;
    isNullable?: boolean;
  };

export type FormSelectInputAsyncPaginateMutationCreatableItemCommonProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  TContext = any,
  TTransformedValues extends FieldValues | undefined = undefined,
  Value = string | number,
  Option extends OptionType<Value> = OptionType<Value>,
  IsMulti extends boolean = boolean,
  Group extends GroupBase<Option> = GroupBase<Option>,
  TData = any,
  TError = ApiError,
  TVariables = void,
  TContext2 = unknown,
> = Omit<
  FormSelectInputAsyncPaginateMutationCreatableProps<
    TFieldValues,
    TName,
    TContext,
    TTransformedValues,
    Value,
    Option,
    IsMulti,
    Group,
    TData,
    TError,
    TVariables,
    TContext2
  >,
  | "form"
  | "name"
  | "label"
  | "mapping"
  | "mutation"
  | "createOptions"
  | "requestVariable"
> &
  Partial<Pick<FormItemCommonProps, "label">>;

export function FormSelectInputAsyncPaginateMutationCreatableItem<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  TContext,
  TTransformedValues extends FieldValues | undefined,
  Value,
  Option extends OptionType<Value>,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
  TData,
  TError,
  TVariables,
  TContext2,
>({
  form,
  name,
  label,
  container,
  assign,
  mapping,
  extract,
  onChange,
  onBlur,
  className,
  classNames: _classNames,
  isActive,
  inline,
  displaySelected,
  isNullable,
  empty,
  ...props
}: FormSelectInputAsyncPaginateMutationCreatableProps<
  TFieldValues,
  TName,
  TContext,
  TTransformedValues,
  Value,
  Option,
  IsMulti,
  Group,
  TData,
  TError,
  TVariables,
  TContext2
>) {
  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const value = useFieldOptionsMapping({
          ...props,
          field,
          assign,
          mapping,
          extract,
          isNullable,
          empty,
          // cache
        } as FieldOptionsMappingProps<
          TFieldValues,
          TName,
          Value,
          Option,
          IsMulti
        >);
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const ext = useFieldOptionsExtract({
          ...props,
          field,
          assign,
          mapping,
          extract,
          isNullable,
          empty,
          // updateCache
        } as FieldOptionsExtractProps<
          TFieldValues,
          TName,
          Value,
          Option,
          IsMulti
        >);
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const classNames = useFormSelectInputCommonClassNames({
          ...props,
          classNames: _classNames,
          isActive,
          inline,
        });
        return (
          <FormItemContent
            {...container}
            label={label}
            inline={container?.inline ?? inline}
          >
            <SelectInputAsyncPaginateMutationCreatable
              debounceTimeout={300}
              className={cn("w-full group/select", className)}
              classNames={classNames}
              {...(field as any)}
              value={value || ""}
              onChange={(v, meta) => {
                const value = ext(v as any);
                const ret = field.onChange(value);
                if (onChange) onChange(v as any, meta as any);
                return ret;
              }}
              onBlur={callAll(field.onBlur, onBlur)}
              getNewOptionData={mapping}
              {...props}
              placeholder={
                inline
                  ? label
                    ? `${label}${props?.isMulti && ((value as any[])?.length ?? 0) > 0 ? ` (${(value as any[])?.length})` : ""}`
                    : undefined
                  : props.placeholder
              }
              controlShouldRenderValue={
                displaySelected
                  ? true
                  : inline
                    ? false
                    : props.controlShouldRenderValue
              }
            />
          </FormItemContent>
        );
      }}
    />
  );
}

export type FormSelectInputAsyncPaginateQueryCreatableProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  TContext,
  TTransformedValues extends FieldValues | undefined,
  Value,
  Option extends OptionType<Value>,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
  TVariables,
  TQueryFnData,
  TError,
  TData,
  TQueryKey extends QueryKey,
  TCacheKey,
> = {
  form: UseFormReturn<TFieldValues, TContext, TTransformedValues>;
  name: TName;
  queryKey?: TCacheKey;
} & FormItemCommonProps &
  Omit<
    SelectInputOptionsMappingProps<TFieldValues, TName, Value, Option, IsMulti>,
    "isMulti"
  > &
  Omit<
    SelectInputAsyncPaginateQueryCreatableProps<
      Value,
      Option,
      IsMulti,
      Group,
      TVariables,
      TQueryFnData,
      TError,
      TData,
      TQueryKey
    >,
    "form"
  > & {
    inline?: boolean;
    displaySelected?: boolean;
    isActive?: boolean;
    isNullable?: boolean;
    searchOnPaste?: boolean;
    /**
     * Set to false when selected options are value === label by construction (e.g. tag
     * names), so the select does not treat them as "partial" and try to resolve each
     * one with a full-list lookup on every value change.
     */
    partialLookup?: boolean;
  };

export type FormSelectInputAsyncPaginateQueryCreatableItemCommonProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  TContext = any,
  TTransformedValues extends FieldValues | undefined = undefined,
  Value = string | number,
  Option extends OptionType<Value> = OptionType<Value>,
  IsMulti extends boolean = boolean,
  Group extends GroupBase<Option> = GroupBase<Option>,
  TVariables = any,
  TQueryFnData = unknown,
  TError = ApiError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
  TCacheKey extends QueryKey | undefined = undefined,
> = Omit<
  FormSelectInputAsyncPaginateQueryCreatableProps<
    TFieldValues,
    TName,
    TContext,
    TTransformedValues,
    Value,
    Option,
    IsMulti,
    Group,
    TVariables,
    TQueryFnData,
    TError,
    TData,
    TQueryKey,
    TCacheKey
  >,
  | "form"
  | "name"
  | "label"
  | "mapping"
  | "queryCreator"
  | "createOptions"
  | "requestVariable"
  | "hasMore"
  | "cacheKey"
> &
  Partial<Pick<FormItemCommonProps, "label">>;

export function FormSelectInputAsyncPaginateQueryCreatableItem<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  TContext,
  TTransformedValues extends FieldValues | undefined,
  Value,
  Option extends OptionType<Value>,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
  TVariables,
  TQueryFnData,
  TError,
  TData,
  TQueryKey extends QueryKey,
  TCacheKey extends QueryKey | undefined,
>({
  form,
  name,
  label,
  container,
  mapping,
  extract,
  queryKey,
  onChange,
  onBlur,
  className,
  classNames: _classNames,
  inline,
  displaySelected,
  isActive,
  isNullable,
  empty,
  searchOnPaste,
  partialLookup,
  ...props
}: FormSelectInputAsyncPaginateQueryCreatableProps<
  TFieldValues,
  TName,
  TContext,
  TTransformedValues,
  Value,
  Option,
  IsMulti,
  Group,
  TVariables,
  TQueryFnData,
  TError,
  TData,
  TQueryKey,
  TCacheKey
>) {
  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => {
        const { cache, updateCache, findPartial, findMissing } =
          // eslint-disable-next-line react-hooks/rules-of-hooks
          useOptionsCache({
            queryKey,
            ...props,
          });
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const value = useFieldOptionsMapping({
          ...props,
          field,
          mapping,
          extract,
          isNullable,
          empty,
          cache,
        } as FieldOptionsMappingProps<
          TFieldValues,
          TName,
          Value,
          Option,
          IsMulti
        >);
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const partial = useFieldOptionsPartial({
          ...props,
          field,
          mapping,
          extract,
          isNullable,
          empty,
          cache,
        } as FieldOptionsCacheProps<
          TFieldValues,
          TName,
          Value,
          Option,
          IsMulti,
          typeof cache
        >);
        // eslint-disable-next-line react-hooks/rules-of-hooks
        React.useEffect(() => {
          if (!mapping || partialLookup === false || partial.length <= 0)
            return;
          findPartial(partial);
        }, [field.value, mapping]);
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const ext = useFieldOptionsExtract({
          ...props,
          field,
          mapping,
          extract,
          isNullable,
          empty,
          isMulti: props?.isMulti,
          updateCache,
        } as FieldOptionsExtractProps<
          TFieldValues,
          TName,
          Value,
          Option,
          IsMulti
        >);
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const classNames = useFormSelectInputCommonClassNames({
          ...props,
          classNames: _classNames,
          isActive,
          inline,
        });
        return (
          <FormItemContent
            {...container}
            label={label}
            inline={container?.inline ?? inline}
          >
            <SelectInputAsyncPaginateQueryCreatable
              debounceTimeout={300}
              className={cn("w-full group/select", className)}
              classNames={classNames}
              {...(field as any)}
              value={value || ""}
              onChange={(v, meta) => {
                const value = ext(v as any);
                const ret =
                  isNullable && (value === null || value === undefined)
                    ? form.resetField(name, { defaultValue: null as any })
                    : field.onChange(value);
                if (onChange) onChange(v as any, meta as any);
                return ret;
              }}
              getNewOptionData={mapping}
              getNewOptionDataAsync={searchOnPaste ? findMissing : undefined}
              {...props}
              placeholder={
                inline
                  ? label
                    ? `${label}${props?.isMulti && ((value as any[])?.length ?? 0) > 0 ? ` (${(value as any[])?.length})` : ""}`
                    : undefined
                  : props.placeholder
              }
              controlShouldRenderValue={
                displaySelected
                  ? true
                  : inline
                    ? false
                    : props.controlShouldRenderValue
              }
            />
          </FormItemContent>
        );
      }}
    />
  );
}
